import React from "react";
import ListItemButton from "@mui/material/ListItemButton";
import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";
import Box from "@mui/material/Box";
import {
  GlobalSearchFormattedData,
  GlobalSearchSelectOption,
} from "data/@types/GlobalSearchData";
import { alpha, Button, Typography } from "@mui/material";

type GlobalSearchSidebarProps = {
  items: GlobalSearchFormattedData[];
  selectedItem: GlobalSearchSelectOption;
  setSelectedItem: (option: GlobalSearchSelectOption) => void;
  openAdvancedView: (option: GlobalSearchSelectOption) => void;
  isLoading: boolean;
  searchTerm: string;
};

const GlobalSearchSidebar = ({
  items,
  selectedItem,
  setSelectedItem,
  openAdvancedView,
  isLoading,
  searchTerm,
}: GlobalSearchSidebarProps) => {
  const requestMade = searchTerm && !isLoading;

  const listStyle: any = {
    fontSize: 11,
    textTransform: "uppercase",
    color: requestMade ? "text.primary" : "text.secondary",
  };

  const totalAmount = items.reduce(
    (partialSum, item) => partialSum + (item.content.length || 0),
    0,
  );
  const isPartialResult = items.some(
    (item) => item.total > item.content.length,
  );

  const handleOpenAdvancedView = (option: GlobalSearchSelectOption): void => {
    openAdvancedView(option);
  };

  return (
    <Box
      className="global-search-sidebar"
      role="complementary"
      data-test-id="gs-sidebar"
      height="100%"
    >
      <List
        dense
        sx={(theme) => ({
          height: "100%",
          backgroundColor: "background.globalSearch.sidebar",
          ".Mui-focusVisible": {
            backgroundColor:
              alpha(theme.palette.text.primary, 0.2) + " !important",
          },
          ".Mui-selected.Mui-focusVisible": {
            backgroundColor:
              alpha(theme.palette.text.primary, 0.2) + " !important",
          },
          ".Mui-focusVisible .MuiListItemText-primary": {
            fontSize: 12,
            fontWeight: 700,
            filter:
              theme.palette.mode === "light" ? "darkness(2)" : "brightness(2)",
            transition: "fontWeight filter .2s",
          },
          ".Mui-selected": { backgroundColor: "transparent !important" },
          ".Mui-selected .MuiListItemText-primary": {
            color: "common.caBlue.lighter",
            fontSize: 12,
            fontWeight: 700,
          },
        })}
      >
        <ListItemButton
          selected={selectedItem === "All"}
          onClick={() => setSelectedItem("All")}
          className="ca-clickable"
        >
          <ListItemText
            primaryTypographyProps={listStyle}
            primary={
              <>
                All
                {searchTerm &&
                  totalAmount > 0 &&
                  ` (${totalAmount}${isPartialResult ? "+" : ""})`}
              </>
            }
            data-test-id="gs-sidebar-text"
          />
        </ListItemButton>
        {items.map(({ name, content, total }) => (
          <ListItemButton
            key={name}
            selected={selectedItem === name}
            onClick={() => {
              setSelectedItem(name);
            }}
            className="ca-clickable"
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
              }}
            >
              <ListItemText
                primaryTypographyProps={listStyle}
                primary={
                  <>
                    {name}
                    {searchTerm &&
                      content.length > 0 &&
                      ` (${content.length}${total > content.length ? "+" : ""})`}
                  </>
                }
                data-test-id="gs-sidebar-text"
              />
              {name === "Meetings" && (
                <>
                  <Typography
                    sx={{
                      padding: "0 0.50em",
                      color: requestMade ? "text.primary" : "text.secondary",
                      fontSize: selectedItem === name ? 12 : 11,
                      fontWeight: selectedItem === name ? 700 : 400,
                    }}
                    component="span"
                  >
                    &gt;
                  </Typography>
                  <Button
                    onClick={() => handleOpenAdvancedView(name)}
                    disableRipple
                    sx={{
                      color: requestMade ? "text.primary" : "text.secondary",
                      fontSize: selectedItem === name ? 12 : 11,
                      fontWeight: selectedItem === name ? 700 : 400,
                      padding: 0,
                      textTransform: "uppercase",
                      backgroundColor: "transparent !important",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "transparent !important",
                        boxShadow: "none",
                        color: "common.caBlue.lighter",
                      },
                    }}
                  >
                    Advanced View
                  </Button>
                </>
              )}
            </Box>
          </ListItemButton>
        ))}
      </List>
    </Box>
  );
};

export default GlobalSearchSidebar;
